import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { AlertCircle } from 'lucide-react';
import { METRICS, getUserColor } from './constants';

const MetricChart = ({ metric, setMetric, loading, error, allUsers, allUserIds, chartData, onGoToChores }) => {
  const activeMetric = METRICS.find(m => m.key === metric);
  const total = chartData.reduce(
    (sum, row) => sum + allUsers.reduce((s, u) => s + (row[u.name] || 0), 0),
    0
  );

  return (
    <>
      {/* Metric Toggle */}
      <div className="grid grid-cols-2 gap-2">
        {METRICS.map(m => {
          const Icon = m.icon;
          return (
            <button
              key={m.key}
              onClick={() => setMetric(m.key)}
              className={`flex items-center gap-2 text-xs font-semibold py-2.5 px-4 rounded-xl border transition-all ${metric === m.key
                  ? 'bg-mint/15 text-mint border-mint/40 shadow-sm'
                  : 'bg-ink-card3 text-ink-fg/50 border-white/10 hover:border-mint/30'
                }`}
            >
              <Icon size={14} />
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Chart */}
      <div className="bg-ink-card rounded-2xl border border-white/10 shadow-sm p-4">
        {!loading && !error && allUsers.length > 0 && (
          <div className="flex items-baseline justify-between mb-3">
            <span className="text-[9.5px] font-mono font-medium text-ink-fg/40 uppercase tracking-widest">
              {activeMetric?.label} per day
            </span>
            <span className="text-lg font-serif text-ink-fg">
              {metric === 'grade' ? '' : Math.round(total)}{activeMetric?.unit}
            </span>
          </div>
        )}
        {loading ? (
          <div className="h-48 flex items-end gap-1.5 px-1">
            {[40, 65, 30, 80, 50, 70, 35, 60, 45, 75, 55, 25, 65, 40].map((h, i) => (
              <div key={i} className="flex-1 rounded-t skeleton-shimmer" style={{ height: `${h}%`, animationDelay: `${(i % 5) * 0.08}s` }} />
            ))}
          </div>
        ) : error ? (
          <div className="h-48 flex flex-col items-center justify-center text-danger gap-2">
            <AlertCircle size={20} />
            <span className="text-sm">{error}</span>
          </div>
        ) : allUsers.length === 0 ? (
          <div className="h-48 flex flex-col items-center justify-center gap-3 text-center px-4">
            <span className="text-ink-fg/45 text-sm italic max-w-[220px]">No chores logged this week. Log one and the bars fill in your color.</span>
            {onGoToChores && (
              <button
                onClick={onGoToChores}
                className="text-xs font-bold text-mint hover:text-ink-fg transition-colors"
              >
                Go to Chores
              </button>
            )}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(245,242,236,.08)" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'rgba(245,242,236,.4)' }} />
              <YAxis tick={{ fontSize: 10, fill: 'rgba(245,242,236,.4)' }} />
              <Tooltip
                contentStyle={{ borderRadius: '12px', border: '1px solid rgba(245,242,236,.1)', background: '#1D1F23', fontSize: 12, color: '#F4F1E9' }}
                labelStyle={{ color: '#F4F1E9' }}
                labelFormatter={(value, payload) => payload[0]?.payload?.fullRange || value}
                formatter={(value, name) => [`${value}${METRICS.find(m => m.key === metric)?.unit ?? ''}`, name]}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(245,242,236,.6)' }} />
              {allUsers.map(user => (
                <Bar
                  key={user.id}
                  dataKey={user.name}
                  stackId="a"
                  fill={getUserColor(user, allUserIds)}
                  radius={[2, 2, 0, 0]}
                  maxBarSize={32}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </>
  );
};

export default MetricChart;
